import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { useSetOnboardingChatCompleted, useUserDetail } from '@/hooks/useUsers';
import { getErrorMessage } from '@/errors/messages';

// The per-user re-test loop for the mandatory onboarding chat (LIM-2845):
// clearing the bit sends the user back through a fresh chat session on their
// next app foreground, setting it lets them past the route guard without
// sitting through the chat. The write is idempotent server-side; each button
// is disabled when the bit already holds the value it would write.
export function OnboardingChatCard({ userId }: { userId: string }) {
  const { data: user } = useUserDetail(userId);
  const setCompleted = useSetOnboardingChatCompleted(userId);
  const snackbar = useSnackbar();

  // The value the open dialog would write, or null when no dialog is open.
  const [pending, setPending] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const completed = user?.isOnboardingChatCompleted ?? false;

  const submit = async (next: boolean) => {
    setError(null);
    try {
      await setCompleted.mutateAsync(next);
      setPending(null);
      snackbar.show(
        next
          ? 'Onboarding chat marked complete — they skip the flow on their next app open'
          : 'Onboarding chat reset — they re-enter the flow on their next app open',
      );
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const open = (next: boolean) => {
    setError(null);
    setPending(next);
  };

  return (
    <Card variant="elevated">
        <Typography variant="h6" sx={{ mb: 2 }}>
          Onboarding Chat
        </Typography>

        <Stack spacing={2} alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase' }}>
              Completed
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusPill
                label={completed ? 'Yes' : 'No'}
                tone={completed ? 'success' : 'neutral'}
                size="sm"
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!completed}
              onClick={() => open(false)}
            >
              Restart onboarding chat
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={completed}
              onClick={() => open(true)}
            >
              Mark chat complete
            </Button>
          </Stack>
        </Stack>

        {pending !== null && (
          <ConfirmationDialog
            open
            title={pending ? 'Mark chat complete' : 'Restart onboarding chat'}
            message={
              pending
                ? 'Sets their completed flag, so the app lets them past the onboarding chat without going through it.'
                : 'Clears their completed flag, so the app sends them through a fresh onboarding chat the next time they open it.'
            }
            confirmLabel={pending ? 'Mark complete' : 'Restart'}
            loading={setCompleted.isPending}
            onConfirm={() => void submit(pending)}
            onCancel={() => setPending(null)}
            data-testid={
              pending ? 'complete-onboarding-chat-dialog' : 'restart-onboarding-chat-dialog'
            }
          >
            <FormErrorBanner error={error ?? undefined} />
          </ConfirmationDialog>
        )}

        <Snackbar {...snackbar.snackbarProps} />
    </Card>
  );
}
